import { CrmEmbedApiError, CrmEmbedClient } from './crm-embed-client'
import { shouldGuideCrmPlatformReview } from '../../shared/crm-review-guidance.js'
import { normalizeCrmSharePageSettings, renderCrmSharePageText } from '../../shared/crm-share-page.js'
import type { CrmSharePageFieldKey } from '../../shared/crm-share-page.js'
import { getCrmEmbedFlowCopyWithGiftName, getCrmPausedAmazonReviewsCopy } from './crm-flow-copy'
import { getCrmEmbedPalette } from './crm-embed-palettes'
import { CrmEmbedNoOrderFlow, isCrmEmbedNoOrderSuccess } from './crm-no-order-flow'
import type {
  CrmEmbedBootstrapResponse,
  CrmEmbedClaimPayload,
  CrmEmbedClaimResult,
  CrmEmbedFlowType,
  CrmEmbedPlatform,
  CrmEmbedProduct,
  CrmEmbedSample,
  CrmEmbedUploadedScreenshot,
} from './crm-embed-types'

export const CRM_EMBED_COMPONENT_VERSION = '1.1.0'

const DEFAULT_API_BASE_URL = new URL(
  /* @vite-ignore */ '/api/crm/embed/v1',
  import.meta.url,
).toString().replace(/\/$/, '')
const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000
const MIN_COMMENT_LENGTH = 10

/** 亚马逊「Your Orders」购买记录页通用链接（分享页跳转设置 = 客户购买记录时使用）。 */
const CRM_AMAZON_ORDER_HISTORY_URL = 'https://www.amazon.com/gp/css/order-history'

/** 亚马逊买家中心（Your Account 账户中心）通用链接（分享页跳转设置 = 客户买家中心时使用）。 */
const CRM_AMAZON_BUYER_CENTER_URL = 'https://www.amazon.com/gp/css/homepage.html'

const platformLabels: Record<CrmEmbedPlatform, string> = {
  amazon: 'Amazon',
  tiktok: 'TikTok',
  walmart: 'Walmart',
  bestbuy: 'Bestbuy',
  official_website: 'Official Website',
}

/** 表单底部条款：各平台官方渠道名称。 */
const promoPlatformNames: Record<CrmEmbedPlatform, string> = {
  amazon: 'Amazon.com',
  tiktok: 'TikTok Shop',
  walmart: 'Walmart',
  bestbuy: 'Bestbuy',
  official_website: 'our Official Website',
}

const orderPlaceholders: Record<CrmEmbedPlatform, string> = {
  amazon: '1xx-xxxxxxx-xxxxxxx',
  tiktok: '5xxxxxxxxxxxxxxxxx',
  walmart: '200xxxxxxxxxxxxxx',
  bestbuy: 'Enter your Bestbuy order ID',
  official_website: 'Enter your website order ID',
}

interface FlowFormState {
  platformKey: CrmEmbedPlatform
  orderId: string
  customerName: string
  customerEmail: string
  phone: string
  productId: string
  sampleId: string
  reviewRating: number | null
  commentText: string
  address: string
}

interface FlowDraft {
  version: 1
  integrationId: string
  flowType: CrmEmbedFlowType
  sourcePath: string
  claimId: string
  flowToken: string
  step: number
  form: FlowFormState
  screenshot: CrmEmbedUploadedScreenshot | null
  updatedAt: string
}

const defaultForm = (): FlowFormState => ({
  platformKey: 'amazon',
  orderId: '',
  customerName: '',
  customerEmail: '',
  phone: '',
  productId: '',
  sampleId: '',
  reviewRating: null,
  commentText: '',
  address: '',
})

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')

const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

// 产品码(XXXX-XXXX-XXXX)与订单号共用输入框,命中产品码格式时走后端产品码验证
const PRODUCT_CODE_PATTERN = /^[0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{1,4}(?:-[0-9A-Z])?$/
const isProductCodeInput = (value: string) => PRODUCT_CODE_PATTERN.test(value.trim())
const normalizeProductCodeInput = (value: string) => value.trim().toUpperCase()

const normalizeFlowType = (value: string): CrmEmbedFlowType => {
  const normalized = value.trim().toLowerCase()
  if (normalized === 'warranty' || normalized === 'registry' || normalized === 'ryo') return normalized
  return 'omb'
}

/**
 * 完成回调 URL：只允许同源相对路径。组件属性可能被宿主页脚本篡改，
 * 绝对 URL/跨域地址一律拒绝，避免把客户数据发往第三方。
 */
const normalizeHostCallbackUrl = (value: string): string => {
  const raw = String(value || '').trim()
  if (!raw) return ''

  try {
    const base = typeof window === 'undefined' ? 'https://crm.local' : window.location.origin
    const url = new URL(raw, base)
    if (typeof window !== 'undefined' && url.origin !== window.location.origin) return ''
    return `${url.pathname}${url.search}`
  } catch {
    return ''
  }
}

const styles = `
  :host {
    --crm-primary: #5d84e8;
    --crm-surface: #f4f9ff;
    --crm-ink: #10233f;
    --crm-muted: #52657e;
    --crm-border: color-mix(in srgb, var(--crm-primary) 22%, #d8e0ec);
    --crm-radius: 16px;
    display: block;
    color: var(--crm-ink);
    font-family: inherit, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  * { box-sizing: border-box; }
  button, input, textarea { font: inherit; }
  button { cursor: pointer; }
  button:disabled { cursor: not-allowed; opacity: .58; }

  .empty-bootstrap { min-height: 1px; }
  .flow-shell { background: var(--crm-surface); padding: clamp(24px, 5vw, 64px) 18px 72px; }
  .flow-container { width: min(1180px, 100%); margin: 0 auto; }
  .brand-row { display: flex; justify-content: space-between; gap: 16px; align-items: center; margin-bottom: 24px; }
  .brand { font-weight: 800; letter-spacing: -.02em; color: var(--crm-ink); }
  .version-badge { border: 1px solid var(--crm-border); border-radius: 999px; padding: 6px 10px; color: var(--crm-muted); font-size: 12px; background: #fff; }

  .progress { margin-bottom: 34px; }
  .progress ol { list-style: none; padding: 0; margin: 0 0 12px; display: grid; grid-template-columns: repeat(var(--step-count), minmax(0, 1fr)); gap: 14px; }
  .progress li { display: flex; align-items: center; gap: 10px; min-width: 0; color: var(--crm-muted); font-size: 13px; font-weight: 700; }
  .progress li span { width: 34px; height: 34px; flex: 0 0 34px; display: grid; place-items: center; border-radius: 50%; border: 1px solid var(--crm-border); background: #fff; }
  .progress li strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .progress li.active, .progress li.complete { color: var(--crm-primary); }
  .progress li.active span, .progress li.complete span { color: #fff; border-color: var(--crm-primary); background: var(--crm-primary); }
  .progress-track { height: 9px; overflow: hidden; border-radius: 999px; background: color-mix(in srgb, var(--crm-primary) 10%, #e6ebf2); }
  .progress-track i { display: block; height: 100%; border-radius: inherit; background: var(--crm-primary); }

  .hero { margin-bottom: 24px; }
  .eyebrow { margin: 0 0 8px; color: var(--crm-primary); font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: .1em; }
  h1 { margin: 0; color: var(--crm-ink); font-size: clamp(32px, 5vw, 58px); line-height: 1.04; letter-spacing: -.045em; }
  .hero > p:last-child { max-width: 860px; margin: 14px 0 0; color: var(--crm-muted); font-size: clamp(16px, 2vw, 19px); line-height: 1.65; }

  .card { width: 100%; border: 1px solid var(--crm-border); border-radius: var(--crm-radius); background: #fff; box-shadow: 0 22px 54px color-mix(in srgb, var(--crm-ink) 10%, transparent); padding: clamp(22px, 4vw, 42px); }
  .form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 22px 20px; }
  .field { display: grid; gap: 8px; color: var(--crm-muted); font-size: 14px; font-weight: 750; }
  .field.full { grid-column: 1 / -1; }
  .required { color: var(--crm-primary); }
  input, textarea { width: 100%; border: 1px solid var(--crm-border); border-radius: 11px; background: #fff; color: var(--crm-ink); outline: none; padding: 14px 15px; min-height: 50px; }
  textarea { min-height: 150px; resize: vertical; line-height: 1.55; }
  input:focus, textarea:focus { border-color: var(--crm-primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--crm-primary) 15%, transparent); }

  .platforms { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 24px; }
  .pill { border: 1px solid var(--crm-border); border-radius: 10px; padding: 10px 17px; background: #fff; color: var(--crm-muted); font-weight: 750; }
  .pill.active { border-color: var(--crm-primary); background: var(--crm-primary); color: #fff; }

  .actions { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-top: 24px; }
  .primary, .secondary, .link-button { min-height: 48px; border-radius: 10px; padding: 0 20px; font-weight: 800; }
  .primary { border: 1px solid var(--crm-primary); background: var(--crm-primary); color: #fff; }
  .secondary { border: 1px solid var(--crm-border); background: #fff; color: var(--crm-ink); }
  .link-button { border: 0; background: transparent; color: var(--crm-primary); padding-inline: 4px; }

  .terms { border-top: 1px solid var(--crm-border); margin-top: 24px; padding-top: 19px; color: var(--crm-muted); font-size: 14px; line-height: 1.65; }
  .terms strong { display: block; color: var(--crm-primary); margin-bottom: 5px; }
  .terms p { margin: 4px 0; }

  .promo-terms { border-top: 1px solid var(--crm-border); margin-top: 24px; padding-top: 19px; color: var(--crm-muted); font-size: 13px; line-height: 1.65; }
  .promo-terms strong { display: block; color: var(--crm-primary); font-size: 14px; margin-bottom: 5px; }
  .promo-terms p { margin: 4px 0; font-weight: 800; }
  .promo-terms ol { display: grid; gap: 6px; margin: 4px 0 0; padding-left: 20px; }

  .alert { border-radius: 11px; padding: 13px 15px; margin: 0 0 20px; font-size: 14px; line-height: 1.5; }
  .alert.error { border: 1px solid #efb4b4; background: #fff4f4; color: #9d2626; }
  .alert.notice { border: 1px solid var(--crm-border); background: var(--crm-surface); color: var(--crm-muted); }

  .product-layout { display: grid; grid-template-columns: minmax(0, 1.45fr) minmax(250px, .55fr); gap: 24px; }
  .options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 15px; }
  .option { min-width: 0; overflow: hidden; padding: 0; border: 1px solid var(--crm-border); border-radius: 13px; background: #fff; color: var(--crm-ink); text-align: left; display: flex; flex-direction: column; justify-content: flex-start; }
  .option.active { border-color: var(--crm-primary); box-shadow: 0 0 0 2px color-mix(in srgb, var(--crm-primary) 24%, transparent); }
  .option-media { aspect-ratio: 1 / 1; display: grid; place-items: center; background: #f8fafc; border-bottom: 1px solid var(--crm-border); }
  .option-media img { width: 100%; height: 100%; object-fit: contain; }
  .image-placeholder { color: var(--crm-muted); font-size: 13px; padding: 12px; text-align: center; }
  .option-copy { display: grid; gap: 4px; padding: 13px; }
  .option-copy strong { overflow-wrap: anywhere; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .option-copy small { color: var(--crm-muted); }

  .summary { border-radius: 13px; background: var(--crm-surface); padding: 20px; align-self: start; }
  .summary span { display: block; color: var(--crm-primary); text-transform: uppercase; letter-spacing: .08em; font-size: 12px; font-weight: 800; }
  .summary strong { display: block; margin-top: 8px; font-size: 18px; overflow-wrap: anywhere; }
  .summary p { margin: 9px 0 0; color: var(--crm-muted); line-height: 1.55; }

  .rating { display: flex; flex-wrap: wrap; gap: 8px; }
  .star { width: 48px; height: 44px; border: 1px solid var(--crm-border); border-radius: 9px; background: #fff; color: #bdc5d0; font-size: 24px; }
  .star.active { border-color: color-mix(in srgb, var(--crm-primary) 55%, #e4ad35); color: #f2a922; background: #fffaf0; }
  .helper { color: var(--crm-muted); font-size: 13px; font-weight: 500; }

  .review-callout { border-radius: 13px; padding: 20px; background: var(--crm-surface); margin-bottom: 20px; }
  .share-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 18px; }
  .share-meta span { border-radius: 999px; padding: 7px 11px; background: var(--crm-primary); color: #fff; font-size: 13px; font-weight: 750; }
  .review-callout h2 { margin: 0 0 8px; font-size: 20px; }
  .review-callout p { color: var(--crm-muted); line-height: 1.6; margin: 6px 0; }
  .comment-preview { white-space: pre-wrap; border: 1px solid var(--crm-border); background: #fff; border-radius: 10px; padding: 15px; color: var(--crm-ink); min-height: 90px; }
  .upload-box { border: 1px dashed var(--crm-border); border-radius: 11px; padding: 18px; margin-top: 16px; }
  .upload-box input { border: 0; padding: 0; min-height: 0; }
  .upload-box p { margin: 8px 0 0; color: var(--crm-muted); font-size: 13px; }

  .shipping-grid { display: grid; grid-template-columns: minmax(220px, .42fr) minmax(0, 1fr); gap: 26px; }
  .selected-sample { border-radius: 13px; background: var(--crm-surface); padding: 20px; }
  .selected-sample .option-media { border: 0; border-radius: 10px; background: #fff; margin: 12px 0; }
  .selected-sample > span { color: var(--crm-primary); font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
  .selected-sample strong { display: block; overflow-wrap: anywhere; }

  .complete { text-align: center; padding: clamp(38px, 7vw, 72px); }
  .check { width: 66px; height: 66px; margin: 0 auto 18px; display: grid; place-items: center; border-radius: 50%; background: var(--crm-primary); color: #fff; font-size: 34px; }
  .complete h1 { font-size: clamp(32px, 5vw, 48px); }
  .complete p { color: var(--crm-muted); line-height: 1.65; max-width: 650px; margin: 14px auto 0; }
  .complete .home-link { display: inline-flex; align-items: center; justify-content: center; min-height: 48px; margin-top: 26px; padding: 0 30px; border-radius: 999px; background: var(--crm-primary); color: #fff; font-weight: 800; text-decoration: none; }

  .congrats { border: 1px solid var(--crm-border); border-radius: var(--crm-radius); padding: clamp(22px, 4vw, 36px); background: color-mix(in srgb, var(--crm-primary) 8%, #fff); text-align: center; margin-bottom: 22px; }
  .congrats .icon { font-size: 44px; line-height: 1; margin-bottom: 8px; }
  .congrats h2 { margin: 0 0 8px; font-size: clamp(24px, 3vw, 32px); }
  .congrats p { margin: 0 auto; max-width: 640px; color: var(--crm-muted); line-height: 1.65; }

  .review-product { display: flex; align-items: center; gap: 14px; border: 1px solid var(--crm-border); border-radius: 13px; padding: 12px 14px; background: var(--crm-surface); margin-bottom: 20px; }
  .review-product img { width: 72px; height: 72px; flex: 0 0 72px; border-radius: 9px; object-fit: contain; background: #fff; }
  .review-product > span { display: grid; gap: 5px; min-width: 0; }
  .review-product strong { overflow-wrap: anywhere; }
  .review-product small { color: var(--crm-muted); overflow-wrap: anywhere; }

  .gift-section { border-top: 1px solid var(--crm-border); margin-top: 24px; padding-top: 22px; }
  .gift-section > h2 { margin: 0 0 6px; font-size: 20px; }
  .gift-section > p { margin: 0 0 16px; color: var(--crm-muted); line-height: 1.6; }

  .fatal { width: min(760px, calc(100% - 36px)); margin: 40px auto; border: 1px solid #efb4b4; border-radius: 14px; padding: 28px; background: #fff; color: #792424; }
  .fatal h2 { margin: 0 0 8px; }
  .fatal p { line-height: 1.55; }

  @media (max-width: 820px) {
    .progress ol { display: flex; }
    .progress li { display: none; }
    .progress li.active { display: flex; flex: 1; }
    .form-grid, .product-layout, .shipping-grid { grid-template-columns: 1fr; }
    .options { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .option-media { aspect-ratio: 16 / 10; }
  }

  @media (max-width: 520px) {
    .flow-shell { padding-inline: 12px; }
    .card { padding: 20px 16px; }
    .options { grid-template-columns: 1fr; gap: 10px; }
    .option { display: grid; grid-template-columns: 64px minmax(0, 1fr); gap: 12px; align-items: center; border-radius: 11px; }
    .option-media { aspect-ratio: 1; border: 0; border-radius: 9px; }
    .option-copy { padding: 10px 10px 10px 0; }
    .option-copy strong { font-size: 14px; }
    .option-copy small { overflow-wrap: anywhere; }
    .brand-row { align-items: flex-start; flex-direction: column; }
    .primary, .secondary { width: 100%; }
  }
`

export class CrmCustomerFlowElement extends HTMLElement {
  private readonly root: ShadowRoot
  private client: CrmEmbedClient | null = null
  private session: CrmEmbedBootstrapResponse | null = null
  private noOrderFlow: CrmEmbedNoOrderFlow | null = null
  private initializeVersion = 0
  private flowType: CrmEmbedFlowType = 'omb'
  private sourcePath = '/omb'
  private integrationId = ''
  private apiBaseUrl = DEFAULT_API_BASE_URL
  private step = 1
  private form = defaultForm()
  private products: CrmEmbedProduct[] = []
  private samples: CrmEmbedSample[] = []
  private samplesLoading = false
  private claimId = ''
  private flowToken = ''
  private screenshot: CrmEmbedUploadedScreenshot | null = null
  private screenshotFile: File | null = null
  private initializing = true
  private submitting = false
  private errorMessage = ''
  private noticeMessage = ''
  private copyConfirmed = false
  private completeCallbackUrl = ''
  private completionReason: 'NO_MATCHED_GIFT' | null = null
  /** 暂停亚马逊评论：第一页表单提交后直接结束流程并展示暂停提示 */
  private amazonReviewsPaused = false

  constructor() {
    super()
    this.root = this.attachShadow({ mode: 'open' })
    this.root.addEventListener('click', (event) => this.handleClick(event))
    this.root.addEventListener('input', (event) => this.handleInput(event))
    this.root.addEventListener('change', (event) => this.handleChange(event))
    this.root.addEventListener('submit', (event) => this.handleSubmit(event))
  }

  connectedCallback() {
    this.initialize().catch((error: unknown) => this.handleFatalError(error))
  }

  disconnectedCallback() {
    this.initializeVersion++
    this.noOrderFlow?.destroy()
    this.noOrderFlow = null
  }

  private get copy() {
    return getCrmEmbedFlowCopyWithGiftName(
      this.flowType,
      this.session?.site.officialWebsiteGiftName || '',
    )
  }

  private get sharePageSettings() {
    return normalizeCrmSharePageSettings(this.session?.site.sharePage)
  }

  private get activeFlowContent() {
    return this.session?.site.flowContents?.[this.flowType]
  }

  private shareVisible(key: CrmSharePageFieldKey) {
    return this.sharePageSettings[key]?.visible !== false
  }

  private shareText(key: CrmSharePageFieldKey, values: Record<string, string | number> = {}, fallback = '') {
    return renderCrmSharePageText(this.sharePageSettings, key, values, fallback)
  }

  private get flowVersion() {
    return this.session?.integration.flowVersion || 'simple'
  }

  private get selectedProduct() {
    return this.products.find((product) => product.id === this.form.productId) || null
  }

  private get verificationInputLabel() {
    return this.session?.platformVerification?.[this.form.platformKey]?.inputLabel || 'Order ID'
  }

  private get verificationInputPlaceholder() {
    const methods = this.session?.platformVerification?.[this.form.platformKey]?.methods || []
    if (methods.length === 1 && methods[0] === 'product_code') return 'XXXX-XXXX-XXXX'
    return orderPlaceholders[this.form.platformKey]
  }

  private get selectedSample() {
    return this.samples.find((sample) => sample.id === this.form.sampleId) || null
  }

  private get reviewEligible() {
    return shouldGuideCrmPlatformReview(this.form.reviewRating, this.session?.site.reviewPageMinRating)
  }

  private get screenshotRequired() {
    return Boolean(
      this.reviewEligible &&
        this.session?.site.screenshotRequiredPlatforms.includes(this.form.platformKey),
    )
  }

  private get reviewTargetLabel() {
    return this.form.platformKey === 'official_website' && this.session?.site.trustpilotUrl
      ? 'Trustpilot'
      : platformLabels[this.form.platformKey]
  }

  /** 分享步骤跳转目标：Amazon 平台按设置跳购买记录页 / 买家中心页，否则保持产品评论页。 */
  private get shareRedirectTarget() {
    if (this.form.platformKey === 'official_website' && this.session?.site.trustpilotUrl) {
      return this.session.site.trustpilotUrl
    }
    const productUrl = this.selectedProduct?.reviewDestinationUrl || ''
    if (this.form.platformKey === 'amazon') {
      const redirectMode = this.session?.site.sharePageRedirectMode
      if (redirectMode === 'purchase_history') {
        return CRM_AMAZON_ORDER_HISTORY_URL
      }
      if (redirectMode === 'buyer_center') {
        return CRM_AMAZON_BUYER_CENTER_URL
      }
    }
    return productUrl
  }

  /** 暂停亚马逊评论：站点开启且当前平台为 Amazon 时，第一页表单提交后直接结束流程。 */
  private get shouldPauseAmazonReviews() {
    return this.session?.site.pauseAmazonReviews === true && this.form.platformKey === 'amazon'
  }

  private get draftKey() {
    return `crm-embed-draft:${this.integrationId}:${this.flowType}:${this.sourcePath}`
  }

  private get analyticsVisitorKey() {
    return `crm-embed-flow-visitor:${this.integrationId}:${this.flowType}`
  }

  private getAnalyticsVisitorId() {
    if (typeof localStorage === 'undefined') return ''
    const existing = String(localStorage.getItem(this.analyticsVisitorKey) || '').trim()
    if (existing) return existing

    const visitorId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem(this.analyticsVisitorKey, visitorId)
    return visitorId
  }

  private trackFlowEvent(eventType: 'form_view' | 'step_one_submitted') {
    const visitorId = this.getAnalyticsVisitorId()
    if (!visitorId || !this.client) return
    void this.client.trackFlowEvent(eventType, visitorId).catch(() => {
      // Analytics must never block the embedded customer flow.
    })
  }

  private initializeConfig() {
    this.integrationId = (this.getAttribute('integration-id') || '').trim()
    this.apiBaseUrl = (this.getAttribute('api-base-url') || DEFAULT_API_BASE_URL).trim()
    const currentPath = typeof window === 'undefined' ? '' : window.location.pathname
    this.sourcePath = (this.getAttribute('source-path') || currentPath || '/omb').trim()
    const pathFlow = this.sourcePath.split('/').filter(Boolean).at(-1) || ''
    this.flowType = normalizeFlowType(this.getAttribute('flow-type') || pathFlow)
    this.completeCallbackUrl = normalizeHostCallbackUrl(this.getAttribute('complete-callback-url') || '')

    if (!this.integrationId) {
      throw new CrmEmbedApiError(
        'The CRM flow is missing its integration ID.',
        400,
        'CRM_EMBED_COMPONENT_CONFIG_INVALID',
      )
    }

    this.client = new CrmEmbedClient({
      integrationId: this.integrationId,
      apiBaseUrl: this.apiBaseUrl,
      flowType: this.flowType,
      sourcePath: this.sourcePath,
      clientVersion: CRM_EMBED_COMPONENT_VERSION,
    })
  }

  private async initialize() {
    const version = ++this.initializeVersion
    this.noOrderFlow?.destroy()
    this.noOrderFlow = null
    this.initializing = true
    this.render()
    this.initializeConfig()
    this.session = await this.client?.bootstrap() || null
    if (!this.isConnected || version !== this.initializeVersion) return
    if (this.client && this.session && (this.session.site.servicePageType === 'no_order_needed'
      || (this.session.capabilities.noOrderNeeded && isCrmEmbedNoOrderSuccess()))) {
      this.initializing = false
      this.noOrderFlow = new CrmEmbedNoOrderFlow(this.root, this, this.client, this.session.site.id)
      await this.noOrderFlow.start()
      if (!this.isConnected || version !== this.initializeVersion) return
      this.dispatchEvent(new CustomEvent('crm-flow-ready', { bubbles: true, composed: true,
        detail: { flowType: 'warranty', servicePageType: 'no_order_needed', siteId: this.session.site.id } }))
      return
    }
    const enabledPlatforms = this.session?.site.enabledPlatforms || []
    if (enabledPlatforms.length && !enabledPlatforms.includes(this.form.platformKey)) {
      this.form.platformKey = enabledPlatforms[0] || 'amazon'
    }
    this.applyTemplatePalette()
    await this.restoreDraft()
    this.trackFlowEvent('form_view')
    this.initializing = false
    this.render()
    this.dispatchEvent(
      new CustomEvent('crm-flow-ready', {
        bubbles: true,
        composed: true,
        detail: {
          flowType: this.flowType,
          flowVersion: this.flowVersion,
          siteId: this.session?.site.id || '',
        },
      }),
    )
  }

  private applyTemplatePalette() {
    const palette = getCrmEmbedPalette(this.session?.site.templateKey)
    const properties = {
      '--crm-primary': palette.primary,
      '--crm-surface': palette.surface,
      '--crm-ink': palette.ink,
      '--crm-muted': palette.muted,
    }
    for (const [property, value] of Object.entries(properties)) {
      if (!this.style.getPropertyValue(property)) this.style.setProperty(property, value)
    }
  }

  private readDraft() {
    if (typeof localStorage === 'undefined') return null
    try {
      const draft = JSON.parse(localStorage.getItem(this.draftKey) || '') as FlowDraft
      if (
        draft.version !== 1 ||
        draft.integrationId !== this.integrationId ||
        draft.flowType !== this.flowType ||
        draft.sourcePath !== this.sourcePath ||
        Date.now() - Date.parse(draft.updatedAt) > DRAFT_MAX_AGE_MS
      ) {
        return null
      }
      return draft
    } catch {
      return null
    }
  }

  private writeDraft() {
    if (typeof localStorage === 'undefined' || !this.claimId || !this.flowToken) return
    const draft: FlowDraft = {
      version: 1,
      integrationId: this.integrationId,
      flowType: this.flowType,
      sourcePath: this.sourcePath,
      claimId: this.claimId,
      flowToken: this.flowToken,
      step: this.step,
      form: this.form,
      screenshot: this.screenshot,
      updatedAt: new Date().toISOString(),
    }
    localStorage.setItem(this.draftKey, JSON.stringify(draft))
  }

  private clearDraft() {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(this.draftKey)
  }

  private async restoreDraft() {
    const draft = this.readDraft()
    if (!draft || !this.client) return

    try {
      const result = await this.client.saveClaim({
        resumeOnly: true,
        claimId: draft.claimId,
        flowToken: draft.flowToken,
      })
      this.applyClaimResult(result)
      this.form = { ...defaultForm(), ...draft.form }
      this.screenshot = draft.screenshot
      if (result.claim) {
        this.form = {
          platformKey: result.claim.platformKey,
          orderId: result.claim.orderId,
          customerName: result.claim.customerName,
          customerEmail: result.claim.customerEmail,
          phone: result.claim.phone,
          productId: result.claim.productId || draft.form.productId,
          sampleId: result.claim.sampleId || draft.form.sampleId,
          reviewRating: result.claim.reviewRating,
          commentText: result.claim.commentText,
          address: result.claim.extraBottleAddress || '',
        }
        if (result.claim.screenshotUrl) {
          this.screenshot = {
            url: result.claim.screenshotUrl,
            name: result.claim.screenshotName || 'review-screenshot',
            mimeType: result.claim.screenshotMimeType || 'image/jpeg',
            bytes: result.claim.screenshotBytes || 1,
          }
        }
      }
      await this.loadProducts()
      if (!this.selectedProduct && result.claim) {
        const externalProductId = result.claim.externalProductId.trim().toLowerCase()
        const productSku = result.claim.productSku.trim().toLowerCase()
        const purchasedProduct = result.claim.purchasedProduct.trim().toLowerCase()
        const matchedProduct = this.products.find(
          (product) =>
            (externalProductId && product.externalProductId.trim().toLowerCase() === externalProductId) ||
            (productSku && product.sku.trim().toLowerCase() === productSku) ||
            (purchasedProduct && product.productName.trim().toLowerCase() === purchasedProduct),
        )
        if (matchedProduct) this.form.productId = matchedProduct.id
      }
      await this.loadSamples()
      if (
        result.stageKey === 'WAITING_STEP_2' &&
        this.selectedProduct &&
        !this.samples.length &&
        (this.flowType === 'warranty' || this.flowType === 'registry')
      ) {
        const completionResult = await this.client.saveClaim(this.claimPayload('WAITING_STEP_2'))
        this.applyClaimResult(completionResult)
        if (this.applyNoMatchedGiftResult(completionResult)) return
      }
      if (result.stageKey === 'COMPLETED') {
        this.completionReason = result.completionReason || result.claim?.completionReason || null
        this.step = 6
        this.clearDraft()
      } else if (result.stageKey === 'WAITING_STEP_2') {
        // 尚未提交留评：回到产品/赠品或留评步骤
        this.step = Math.min(Math.max(draft.step, 2), 3)
      } else {
        // 留评已提交（评分+评论达到最低字符）→ 回到分享步骤；
        // 否则（仅进入过留评页即被保存，评价内容缺失）回到留评步骤补填，避免跳过留评。
        const hasReviewContent =
          Number(result.claim?.reviewRating) >= 1 &&
          Number(result.claim?.reviewRating) <= 5 &&
          String(result.claim?.commentText || '').trim().length >= MIN_COMMENT_LENGTH
        this.step = hasReviewContent ? 4 : 3
      }
      this.noticeMessage = `We restored your unfinished ${this.copy.processName}.`
    } catch {
      this.clearDraft()
      this.claimId = ''
      this.flowToken = ''
      this.form = defaultForm()
      this.step = 1
    }
  }

  private async loadProducts() {
    if (!this.client || !this.form.orderId) return
    if (isProductCodeInput(this.form.orderId)) {
      const verified = await this.verifyProductCode(normalizeProductCodeInput(this.form.orderId))
      this.products = verified.valid
        ? verified.products?.length
          ? verified.products
          : verified.product
            ? [verified.product]
            : []
        : []
    } else {
      this.products = await this.client.getProducts(this.form.platformKey, this.form.orderId)
    }
    if (!this.form.productId && this.products.length === 1) {
      this.form.productId = this.products[0]?.id || ''
    }
  }

  private async loadSamples() {
    const product = this.selectedProduct
    if (!this.client || !product?.externalProductId) {
      this.samples = []
      this.samplesLoading = false
      return
    }
    this.samplesLoading = true
    try {
      this.samples = await this.client.getSamples(this.form.platformKey, product.externalProductId)
    } finally {
      this.samplesLoading = false
    }
  }

  private applyClaimResult(result: CrmEmbedClaimResult) {
    this.claimId = result.claimId || this.claimId
    this.flowToken = result.flowToken || this.flowToken
  }

  private finishFlow(completionReason: 'NO_MATCHED_GIFT' | null = null) {
    if (this.step === 6 && this.completionReason === completionReason) return

    this.completionReason = completionReason
    this.step = 6
    this.clearDraft()
    this.dispatchEvent(
      new CustomEvent('crm-flow-complete', {
        bubbles: true,
        composed: true,
        detail: {
          claimId: this.claimId,
          flowType: this.flowType,
          completionReason,
        },
      }),
    )
    void this.notifyHostCompletion()
  }

  private applyNoMatchedGiftResult(result: CrmEmbedClaimResult) {
    if (result.stageKey !== 'COMPLETED' || result.completionReason !== 'NO_MATCHED_GIFT') {
      return false
    }

    this.finishFlow('NO_MATCHED_GIFT')
    return true
  }

  private claimPayload(stageKey: CrmEmbedClaimPayload['stageKey']): CrmEmbedClaimPayload {
    const product = this.selectedProduct
    const sample = this.selectedSample
    return {
      claimId: this.claimId || undefined,
      flowToken: this.flowToken || undefined,
      stageKey,
      platformKey: this.form.platformKey,
      orderId: this.form.orderId.trim(),
      customerName: this.form.customerName.trim(),
      customerEmail: this.form.customerEmail.trim(),
      phone: this.form.phone.trim(),
      productId: product?.id,
      purchasedProduct: product?.productName,
      reviewRating: this.form.reviewRating,
      commentText: this.form.commentText.trim(),
      reviewDestinationUrl: product?.reviewDestinationUrl,
      screenshotUrl: this.screenshot?.url,
      screenshotName: this.screenshot?.name,
      screenshotMimeType: this.screenshot?.mimeType,
      screenshotBytes: this.screenshot?.bytes,
      extraBottleAddress: this.form.address.trim(),
      selectedSampleId: sample?.id,
      selectedSampleTitle: sample?.title,
      selectedSampleSku: sample?.sku,
      selectedSampleImageUrl: sample?.imageUrl,
    }
  }

  private async submitActivation() {
    if (!this.client) return
    if (!this.form.orderId.trim() || !this.form.customerName.trim() || !isEmail(this.form.customerEmail)) {
      throw new CrmEmbedApiError('Please enter a valid order ID, name and email address.', 400)
    }

    const result = await this.client.saveClaim(this.claimPayload('WAITING_STEP_2'))
    this.applyClaimResult(result)
    this.trackFlowEvent('step_one_submitted')
    this.products = Array.isArray(result.products) ? result.products : []
    if (!this.form.productId && this.products.length === 1) {
      this.form.productId = this.products[0]?.id || ''
    }
    if (this.applyNoMatchedGiftResult(result)) return
    if (this.shouldPauseAmazonReviews) {
      this.amazonReviewsPaused = true
      this.finishFlow()
      return
    }
    this.step = 2
    if (this.form.productId) await this.loadSamples()
    this.writeDraft()
  }

  private async verifyProductCode(
    code: string,
  ): Promise<{
    valid: boolean
    used?: boolean
    product?: CrmEmbedProduct | null
    products?: CrmEmbedProduct[]
    requiresProductSelection?: boolean
    message?: string
  }> {
    if (!this.client) {
      throw new CrmEmbedApiError('CRM integration is not ready.', 503)
    }
    const payload = await this.client.verifyProductCode(this.form.platformKey, code)
    return {
      valid: payload.valid === true,
      used: payload.used === true,
      product: (payload.product as CrmEmbedProduct | null) || null,
      products: Array.isArray(payload.products) ? (payload.products as CrmEmbedProduct[]) : [],
      requiresProductSelection: payload.requiresProductSelection === true,
      message: String(payload.message || ''),
    }
  }

  private async submitExperience() {
    if (!this.client || !this.selectedProduct) {
      throw new CrmEmbedApiError('Please choose the product connected to this order.', 400)
    }
    if (this.samples.length && !this.selectedSample) {
      throw new CrmEmbedApiError(`Please choose one of the available ${this.copy.samplePlural}.`, 400)
    }
    const result = await this.client.saveClaim(this.claimPayload('WAITING_STEP_2'))
    this.applyClaimResult(result)
    if (this.applyNoMatchedGiftResult(result)) return
    this.step = 3
    this.writeDraft()
  }

  private async submitReview() {
    if (!this.form.reviewRating) {
      throw new CrmEmbedApiError('Please select a product rating.', 400)
    }
    if (this.form.commentText.trim().length < MIN_COMMENT_LENGTH) {
      throw new CrmEmbedApiError(
        `Please enter at least ${MIN_COMMENT_LENGTH} characters about your experience.`,
        400,
      )
    }
    if (!this.client) return
    const result = await this.client.saveClaim(this.claimPayload('WAITING_LAST_STEP'))
    this.applyClaimResult(result)
    this.step = 4
    this.writeDraft()
  }

  private async ensureScreenshot() {
    if (!this.screenshotRequired || this.screenshot) return
    if (!this.screenshotFile || !this.client || !this.session) {
      throw new CrmEmbedApiError('Please upload a screenshot of your platform comment.', 400)
    }
    this.screenshot = await this.client.uploadScreenshot(this.screenshotFile, this.session.site.id)
  }

  private validateAddress() {
    if (!this.form.customerName.trim() || !isEmail(this.form.customerEmail)) {
      throw new CrmEmbedApiError('Please enter a valid full name and email address.', 400)
    }
    if (!this.form.address.trim()) {
      throw new CrmEmbedApiError(`Please enter the shipping address for your ${this.copy.sampleSingular}.`, 400)
    }
  }

  private async submitShare() {
    if (!this.client) return
    await this.ensureScreenshot()
    const result = await this.client.saveClaim(this.claimPayload('WAITING_LAST_STEP'))
    this.applyClaimResult(result)
    this.step = 5
    this.writeDraft()
  }

  private async completeFlow() {
    if (!this.client) return
    this.validateAddress()
    await this.ensureScreenshot()
    const result = await this.client.saveClaim(this.claimPayload('COMPLETED'))
    this.applyClaimResult(result)
    this.finishFlow()
  }

  private async selectProduct(productId: string) {
    if (!this.client) return

    this.form.productId = productId
    this.form.sampleId = ''
    this.samples = []
    await this.loadSamples()
    if (
      this.samples.length ||
      (this.flowType !== 'warranty' && this.flowType !== 'registry')
    ) {
      return
    }

    const result = await this.client.saveClaim(this.claimPayload('WAITING_STEP_2'))
    this.applyClaimResult(result)
    this.applyNoMatchedGiftResult(result)
  }

  /**
   * 宿主站完成回调：把已完成 claim 的关键字段 POST 给同源端点
   * （例如 neatiquebeauty.com 的积分发放接口）。失败重试一次，绝不阻塞/破坏客户流程。
   */
  private async notifyHostCompletion() {
    const callbackUrl = this.completeCallbackUrl
    if (!callbackUrl) return

    const product = this.selectedProduct
    const sample = this.selectedSample
    const payload = {
      claimId: this.claimId,
      flowType: this.flowType,
      platformKey: this.form.platformKey,
      orderId: this.form.orderId.trim(),
      customerName: this.form.customerName.trim(),
      customerEmail: this.form.customerEmail.trim(),
      phone: this.form.phone.trim(),
      purchasedProduct: product?.productName || '',
      reviewRating: this.form.reviewRating,
      commentText: this.form.commentText.trim(),
      reviewDestinationUrl: product?.reviewDestinationUrl || '',
      screenshotUrl: this.screenshot?.url || '',
      screenshotName: this.screenshot?.name || '',
      selectedSampleId: sample?.id || '',
      selectedSampleTitle: sample?.title || '',
      selectedSampleSku: sample?.sku || '',
      selectedSampleImageUrl: sample?.imageUrl || '',
      completionReason: this.completionReason || '',
    }

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetch(callbackUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        })
        if (response.ok) return
      } catch {
        // Host callback failures must never break the completed customer flow.
      }
    }
  }

  private async runSubmission(action: () => Promise<void>) {
    if (this.submitting) return
    this.submitting = true
    this.errorMessage = ''
    this.noticeMessage = ''
    this.render()
    try {
      await action()
      this.dispatchEvent(
        new CustomEvent('crm-flow-step', {
          bubbles: true,
          composed: true,
          detail: { step: this.step, flowType: this.flowType },
        }),
      )
    } catch (error) {
      this.handleInlineError(error)
    } finally {
      this.submitting = false
      this.render()
    }
  }

  private flowAwareMessage(message: string) {
    if (this.flowType === 'warranty') return message.replace(/registry/gi, 'warranty activation')
    if (this.flowType === 'omb') return message.replace(/registry/gi, 'OMB claim')
    if (this.flowType === 'ryo') return message.replace(/registry/gi, 'RYO claim')
    return message
  }

  private handleInlineError(error: unknown) {
    const message = error instanceof Error ? error.message : 'The request could not be completed.'
    this.errorMessage = this.flowAwareMessage(message)
    this.dispatchEvent(
      new CustomEvent('crm-flow-error', {
        bubbles: true,
        composed: true,
        detail: {
          code: error instanceof CrmEmbedApiError ? error.code : 'CRM_EMBED_UI_ERROR',
          message: this.errorMessage,
          flowType: this.flowType,
        },
      }),
    )
  }

  private handleFatalError(error: unknown) {
    this.initializing = false
    this.handleInlineError(error)
    this.render()
  }

  private resetFlow() {
    this.clearDraft()
    this.step = 1
    this.form = defaultForm()
    this.products = []
    this.samples = []
    this.claimId = ''
    this.flowToken = ''
    this.screenshot = null
    this.screenshotFile = null
    this.errorMessage = ''
    this.noticeMessage = ''
    this.copyConfirmed = false
    this.completionReason = null
    this.render()
  }

  private async copyComment() {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(this.form.commentText)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = this.form.commentText
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.append(textarea)
      textarea.select()
      const copied = document.execCommand('copy')
      textarea.remove()
      if (!copied) {
        throw new CrmEmbedApiError('The comment could not be copied. Please select and copy it manually.')
      }
    }
    this.copyConfirmed = true
    this.noticeMessage = this.shareVisible('copyStatus')
      ? this.shareText('copyStatus', {}, 'Comment copied. You can now paste it on the review page.')
      : ''
    this.render()
  }

  private handleClick(event: Event) {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]')
    if (!target) return
    const action = target.dataset.action || ''

    if (action === 'platform') {
      this.form.platformKey = target.dataset.value as CrmEmbedPlatform
      this.form.productId = ''
      this.form.sampleId = ''
      this.products = []
      this.samples = []
      this.render()
      return
    }
    if (action === 'product') {
      void this.runSubmission(() => this.selectProduct(target.dataset.value || ''))
      return
    }
    if (action === 'sample') {
      this.form.sampleId = target.dataset.value || ''
      this.render()
      return
    }
    if (action === 'rating') {
      this.form.reviewRating = Number(target.dataset.value) || null
      this.render()
      return
    }
    if (action === 'copy-comment') {
      this.copyComment().catch((error: unknown) => {
        this.handleInlineError(error)
        this.render()
      })
      return
    }
    if (action === 'share-back') {
      this.step = 3
      this.noticeMessage = ''
      this.render()
      return
    }
    if (action === 'restart') {
      this.resetFlow()
      return
    }
    if (action === 'retry') {
      this.errorMessage = ''
      this.initialize().catch((error: unknown) => this.handleFatalError(error))
    }
  }

  private handleInput(event: Event) {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement | null
    const field = input?.dataset.field as keyof FlowFormState | undefined
    if (!input || !field || field === 'reviewRating') return
    const value = input.value
    if (field === 'platformKey') {
      this.form.platformKey = value as CrmEmbedPlatform
    } else {
      this.form[field] = value as never
    }
  }

  private handleChange(event: Event) {
    const input = event.target as HTMLInputElement | null
    if (input?.dataset.field !== 'screenshot') return
    this.screenshotFile = input.files?.[0] || null
    this.screenshot = null
    this.render()
  }

  private handleSubmit(event: Event) {
    const form = event.target as HTMLFormElement | null
    if (!form?.dataset.stepForm) return
    event.preventDefault()
    const action = form.dataset.stepForm
    if (action === 'activation') void this.runSubmission(() => this.submitActivation())
    if (action === 'experience') void this.runSubmission(() => this.submitExperience())
    if (action === 'review') void this.runSubmission(() => this.submitReview())
    if (action === 'share') void this.runSubmission(() => this.submitShare())
    if (action === 'complete') void this.runSubmission(() => this.completeFlow())
  }

  private renderProgress() {
    const legacyLabels = this.activeFlowContent?.reviewProgressLabels || ['Review', 'Share', 'Address']
    const labels = this.step !== 4 ? [...legacyLabels] : (['progressReview', 'progressShare', 'progressAddress'] as const)
      .map((key, index) => this.shareVisible(key)
        ? this.shareText(key, {}, legacyLabels[index] || ['Review', 'Share', 'Address'][index])
        : '')
    const visibleStep = Math.min(Math.max(this.step - 2, 0), 3)
    const percent = Math.max(0, Math.min(100, visibleStep * 33.34))

    return `
      <nav class="progress" aria-label="Review progress" style="--step-count:${labels.length}">
        <ol>
          ${labels
            .map(
              (label, index) => `
                <li class="${index + 1 === visibleStep ? 'active' : index + 1 < visibleStep ? 'complete' : ''}" ${index + 1 === visibleStep ? 'aria-current="step"' : ''}>
                  <span>${index + 1 < visibleStep ? '✓' : index + 1}</span>
                  ${label ? `<strong>${escapeHtml(label)}</strong>` : ''}
                </li>
              `,
            )
            .join('')}
        </ol>
        <div class="progress-track" aria-hidden="true"><i style="width:${percent}%"></i></div>
      </nav>
    `
  }

  private renderAlerts() {
    return `
      ${this.errorMessage ? `<div class="alert error" role="alert">${escapeHtml(this.errorMessage)}</div>` : ''}
      ${this.noticeMessage ? `<div class="alert notice" role="status">${escapeHtml(this.noticeMessage)}</div>` : ''}
    `
  }

  private renderPlatformTabs() {
    const platforms = this.session?.site.enabledPlatforms || []
    return `
      <div class="platforms" role="group" aria-label="Purchase platform">
        ${platforms
          .map(
            (platform) => `
              <button type="button" class="pill ${this.form.platformKey === platform ? 'active' : ''}" data-action="platform" data-value="${platform}">
                ${escapeHtml(platformLabels[platform])}
              </button>
            `,
          )
          .join('')}
      </div>
    `
  }

  /** 表单底部 Conditions Apply：一句话概括全部启用平台（不只限于 Amazon）。 */
  private renderPromoConditions() {
    const defaultPlatforms: CrmEmbedPlatform[] = [
      'amazon',
      'tiktok',
      'walmart',
      'bestbuy',
      'official_website',
    ]
    const platforms = (this.session?.site.enabledPlatforms || defaultPlatforms) as CrmEmbedPlatform[]
    const names = platforms.map(
      (platform) =>
        promoPlatformNames[platform] || platformLabels[platform] || platform,
    )
    const platformList =
      names.length <= 1 ? names[0] || '' : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    const contactEmail = escapeHtml(this.session?.site.contactEmail || 'customer service')
    const conditions = [
      `Offer valid only for customers that bought the product at FULL price at our Official Seller on ${platformList} in US market.`,
      `Orders out of US market and out of the platforms above, can contact ${contactEmail} to get support.`,
      'Limited to one backup pair per account and household.',
    ]
    return conditions.map((condition) => `<li>${escapeHtml(condition)}</li>`).join('')
  }

  private renderActivation() {
    return `
      <form class="card" data-step-form="activation">
        ${this.renderAlerts()}
        ${this.renderPlatformTabs()}
        <div class="form-grid">
          <label class="field">
            <span>${escapeHtml(this.verificationInputLabel)} <b class="required">(Required)</b></span>
            <input data-field="orderId" value="${escapeHtml(this.form.orderId)}" placeholder="${escapeHtml(this.verificationInputPlaceholder)}" autocomplete="off" required />
          </label>
          <label class="field">
            <span>Name <b class="required">(Required)</b></span>
            <input data-field="customerName" value="${escapeHtml(this.form.customerName)}" autocomplete="name" required />
          </label>
          <label class="field">
            <span>Email <b class="required">(Required)</b></span>
            <input data-field="customerEmail" type="email" value="${escapeHtml(this.form.customerEmail)}" autocomplete="email" required />
          </label>
          <label class="field">
            <span>Phone</span>
            <input data-field="phone" type="tel" value="${escapeHtml(this.form.phone)}" autocomplete="tel" />
          </label>
        </div>
        <div class="actions">
          <button class="primary" type="submit" ${this.submitting ? 'disabled' : ''}>
            ${this.submitting ? 'Submitting…' : escapeHtml(this.copy.activationButton)}
          </button>
        </div>
        <div class="terms">
          <strong>How to find your order ID?</strong>
          <p>We only use these details to match your order and continue your ${escapeHtml(this.copy.processName)}.</p>
        </div>
        <div class="promo-terms">
          <strong>NO Shipping Charges, NO Hidden Fees, NO Credit Card Required!</strong>
          <p>* Conditions Apply:</p>
          <ol>
            ${this.renderPromoConditions()}
          </ol>
        </div>
      </form>
    `
  }

  private renderProductOptions() {
    if (!this.products.length) {
      return `<div class="alert notice">No eligible product is currently available. Please check your order ID or contact ${escapeHtml(this.session?.site.contactEmail || 'customer service')}.</div>`
    }
    if (this.products.length === 1) {
      const product = this.products[0]
      return `
        <div class="options">
          <div class="option active" aria-label="Matched product">
            <span class="option-media">
              ${product.imageUrl ? `<img src="${escapeHtml(product.imageUrl)}" alt="" loading="lazy" />` : '<span class="image-placeholder">Product image</span>'}
            </span>
            <span class="option-copy">
              <strong>${escapeHtml(product.productName)}</strong>
              <small>${escapeHtml(product.externalProductId || '')}</small>
              <small>Matched automatically.</small>
            </span>
          </div>
        </div>
      `
    }
    return `
      <div class="options">
        ${this.products
          .map(
            (product) => `
              <button type="button" class="option ${this.form.productId === product.id ? 'active' : ''}" data-action="product" data-value="${escapeHtml(product.id)}">
                <span class="option-media">
                  ${product.imageUrl ? `<img src="${escapeHtml(product.imageUrl)}" alt="" loading="lazy" />` : '<span class="image-placeholder">Product image</span>'}
                </span>
                <span class="option-copy">
                  <strong>${escapeHtml(product.productName)}</strong>
                  <small>${escapeHtml(product.externalProductId || '')}</small>
                </span>
              </button>
            `,
          )
          .join('')}
      </div>
    `
  }

  private renderCongrats() {
    return `
      <form class="card" data-step-form="experience">
        ${this.renderAlerts()}
        <section class="congrats" aria-live="polite">
          <div class="icon" aria-hidden="true">🎉</div>
          <h2>Congratulations!</h2>
          <p>${escapeHtml(this.copy.congratsMessage)}</p>
        </section>
        <div class="product-layout">
          <div>
            <label class="field full">
              <span>Choose the product connected to this order <b class="required">(Required)</b></span>
              ${this.renderProductOptions()}
            </label>
            <section class="gift-section">
              <h2>${escapeHtml(this.copy.sampleTitle)}</h2>
              <p>${escapeHtml(this.copy.sampleDescription)}</p>
              ${this.renderSampleOptions()}
            </section>
          </div>
          <aside class="summary">
            <span>Order</span>
            <strong>${escapeHtml(this.form.orderId)}</strong>
            <p>${escapeHtml(platformLabels[this.form.platformKey])}</p>
            <span style="margin-top:18px">Customer</span>
            <strong>${escapeHtml(this.form.customerName)}</strong>
            <p>${escapeHtml(this.form.customerEmail)}</p>
          </aside>
        </div>
        <div class="actions">
          <button class="primary" type="submit" ${this.submitting || !this.products.length ? 'disabled' : ''}>
            ${this.submitting ? 'Saving…' : 'I Want It!'}
          </button>
          <button class="link-button" type="button" data-action="restart">Start over</button>
        </div>
      </form>
    `
  }

  private renderSampleOptions() {
    if (this.samplesLoading) {
      return `<div class="alert notice">Loading options…</div>`
    }
    if (!this.samples.length) {
      return `
        <div class="alert notice">
          No ${escapeHtml(this.copy.samplePlural)} are currently configured for this product. You can continue without selecting one.
        </div>
      `
    }
    return `
      <div class="options">
        ${this.samples
          .map(
            (sample) => `
              <button type="button" class="option ${this.form.sampleId === sample.id ? 'active' : ''}" data-action="sample" data-value="${escapeHtml(sample.id)}">
                <span class="option-media">
                  ${sample.imageUrl ? `<img src="${escapeHtml(sample.imageUrl)}" alt="" loading="lazy" />` : '<span class="image-placeholder">Sample image</span>'}
                </span>
                <span class="option-copy">
                  <strong>${escapeHtml(sample.title)}</strong>
                  <small>${escapeHtml(sample.sku)}</small>
                </span>
              </button>
            `,
          )
          .join('')}
      </div>
    `
  }

  private renderReview() {
    const product = this.selectedProduct
    return `
      <form class="card" data-step-form="review">
        ${this.renderAlerts()}
        <div class="review-product">
          ${product?.imageUrl ? `<img src="${escapeHtml(product.imageUrl)}" alt="" />` : '<span class="image-placeholder">Product image</span>'}
          <span>
            <strong>${escapeHtml(product?.productName || '')}</strong>
            <small>${escapeHtml(product?.externalProductId || '')}</small>
          </span>
        </div>
        <label class="field full">
          <span>How do you like this product? <b class="required">(Required)</b></span>
          <span class="rating" role="radiogroup" aria-label="Product rating">
            ${[1, 2, 3, 4, 5]
              .map(
                (rating) => `
                  <button type="button" class="star ${Number(this.form.reviewRating) >= rating ? 'active' : ''}" data-action="rating" data-value="${rating}" role="radio" aria-checked="${this.form.reviewRating === rating}" aria-label="${rating} star${rating === 1 ? '' : 's'}">★</button>
                `,
              )
              .join('')}
          </span>
        </label>
        <label class="field full" style="margin-top:22px">
          <span>Tell us about your experience <b class="required">(Required)</b></span>
          <textarea data-field="commentText" minlength="${MIN_COMMENT_LENGTH}" required>${escapeHtml(this.form.commentText)}</textarea>
          <small class="helper">Please enter at least ${MIN_COMMENT_LENGTH} characters.</small>
        </label>
        <div class="actions">
          <button class="primary" type="submit" ${this.submitting ? 'disabled' : ''}>
            ${this.submitting ? 'Saving…' : 'Continue'}
          </button>
          <button class="link-button" type="button" data-action="restart">Start over</button>
        </div>
      </form>
    `
  }

  private renderReviewSection() {
    if (!this.reviewEligible) {
      return `
        <div class="review-callout">
          ${this.shareVisible('noExternalInstruction')
            ? `<p>${escapeHtml(this.shareText('noExternalInstruction', {
              processName: this.activeFlowContent?.processName || this.copy.processName,
              platform: platformLabels[this.form.platformKey],
            }, 'Thank you for your feedback. Your honest product feedback has been saved. You can continue to the shipping details.'))}</p>`
            : ''}
        </div>
      `
    }

    const reviewUrl = this.shareRedirectTarget
    const reviewTarget = this.reviewTargetLabel
    return `
      <div class="review-callout">
        ${this.shareVisible('externalInstruction')
          ? `<p>${escapeHtml(this.shareText('externalInstruction', {
            reviewTarget,
            platform: reviewTarget,
            processName: this.activeFlowContent?.processName || this.copy.processName,
            continuation: this.activeFlowContent?.reviewContinuation || this.copy.processName,
          }, this.shareVisible('outboundButton')
            ? `Share the comment you already wrote. Copy your comment, open the review page, and paste the same text there before continuing your ${this.copy.processName}.`
            : 'Your feedback is saved. Continue to enter your shipping address.'))}</p>`
          : ''}
        ${this.shareVisible('commentLabel')
          ? `<small>${escapeHtml(this.shareText('commentLabel', {}, 'Your comment'))}</small>`
          : ''}
        <div class="comment-preview">${escapeHtml(this.form.commentText)}</div>
        ${this.shareVisible('returnInstruction')
          ? `<p>${escapeHtml(this.shareText('returnInstruction', { reviewTarget, platform: reviewTarget },
            this.shareVisible('outboundButton')
              ? `After the ${reviewTarget} page opens, return to this tab to enter your shipping address.`
              : 'Continue to enter your shipping address.'))}</p>`
          : ''}
        ${this.shareVisible('outboundButton')
          ? `<div class="actions">
              ${this.copyConfirmed
                ? this.shareVisible('copiedButton')
                  ? `<button class="secondary" type="button" data-action="copy-comment">${escapeHtml(this.shareText('copiedButton', {}, 'Comment Copied'))}</button>`
                  : ''
                : this.shareVisible('copyButton')
                  ? `<button class="secondary" type="button" data-action="copy-comment">${escapeHtml(this.shareText('copyButton', {}, 'Copy Comment'))}</button>`
                  : ''}
              ${reviewUrl ? `<a class="primary" href="${escapeHtml(reviewUrl)}" target="_blank" rel="noopener noreferrer" referrerpolicy="no-referrer" style="display:inline-grid;place-items:center;text-decoration:none">${escapeHtml(this.shareText('outboundButton', { reviewTarget, platform: reviewTarget }, 'Open Review Page'))}</a>` : ''}
            </div>`
          : ''}
        ${
          this.screenshotRequired
            ? `
              <label class="upload-box field">
                ${this.shareVisible('screenshotInstruction')
                  ? `<span>${escapeHtml(this.shareText('screenshotInstruction', {}, 'Review screenshot (Required)'))}</span>`
                  : ''}
                <input data-field="screenshot" type="file" aria-label="${escapeHtml(this.shareText('screenshotInstruction', {}, 'Upload review screenshot'))}" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif" />
                ${this.screenshot?.name || this.screenshotFile?.name
                  ? `<p>${escapeHtml(this.submitting && this.screenshotFile && !this.screenshot && this.shareVisible('screenshotUploading')
                    ? this.shareText('screenshotUploading', {}, 'Uploading screenshot...')
                    : this.screenshot?.name || this.screenshotFile?.name || '')}</p>`
                  : this.shareVisible('screenshotHint')
                    ? `<p>${escapeHtml(this.shareText('screenshotHint', { maxSize: '5MB' }, 'Upload a JPG, PNG, WEBP, GIF, HEIC or HEIF image up to 5MB.'))}</p>`
                    : ''}
              </label>
            `
            : ''
        }
      </div>
    `
  }

  private renderShare() {
    const platform = platformLabels[this.form.platformKey]
    const meta = [
      this.shareVisible('platformChip') ? this.shareText('platformChip', { platform }, platform) : '',
      this.shareVisible('orderChip') ? this.shareText('orderChip', { orderId: this.form.orderId }, `Order ${this.form.orderId}`) : '',
      this.shareVisible('ratingChip') ? this.shareText('ratingChip', { rating: this.form.reviewRating || '' }, `${this.form.reviewRating} Star Review`) : '',
      this.selectedSample && this.shareVisible('giftChip')
        ? this.shareText('giftChip', { giftName: this.selectedSample.title }, this.selectedSample.title)
        : '',
    ].filter(Boolean)
    return `
      <form class="card" data-step-form="share">
        ${this.renderAlerts()}
        ${meta.length ? `<div class="share-meta">${meta.map((item) => `<span>${escapeHtml(item)}</span>`).join('')}</div>` : ''}
        ${this.renderReviewSection()}
        <div class="actions">
          <button class="secondary" type="button" data-action="share-back">${escapeHtml(this.shareText('backButton', {}, 'Back'))}</button>
          <button class="primary" type="submit" ${this.submitting ? 'disabled' : ''}>
            ${this.submitting ? 'Saving…' : escapeHtml(this.shareText('continueButton', {}, 'Continue to Address'))}
          </button>
        </div>
      </form>
    `
  }

  private renderAddressFields() {
    return `
      <div class="form-grid">
        <label class="field">
          <span>Full Name <b class="required">(Required)</b></span>
          <input data-field="customerName" value="${escapeHtml(this.form.customerName)}" autocomplete="name" required />
        </label>
        <label class="field">
          <span>Email <b class="required">(Required)</b></span>
          <input data-field="customerEmail" type="email" value="${escapeHtml(this.form.customerEmail)}" autocomplete="email" required />
        </label>
        <label class="field full">
          <span>Full Shipping Address <b class="required">(Required)</b></span>
          <textarea data-field="address" autocomplete="street-address" placeholder="Address line, city, state, postal code and country." required>${escapeHtml(this.form.address)}</textarea>
        </label>
        <label class="field full">
          <span>Phone</span>
          <input data-field="phone" type="tel" value="${escapeHtml(this.form.phone)}" autocomplete="tel" />
        </label>
      </div>
    `
  }

  private renderSelectedSample() {
    const sample = this.selectedSample
    const product = this.selectedProduct
    return `
      <aside class="selected-sample">
        <span>${sample ? `Selected ${escapeHtml(this.copy.sampleSingular)}` : 'Selected product'}</span>
        <div class="option-media">
          ${
            sample?.imageUrl || product?.imageUrl
              ? `<img src="${escapeHtml(sample?.imageUrl || product?.imageUrl || '')}" alt="" loading="lazy" />`
              : '<span class="image-placeholder">Product image</span>'
          }
        </div>
        <strong>${escapeHtml(sample?.title || product?.productName || '')}</strong>
      </aside>
    `
  }

  private renderAddress(includeReview: boolean) {
    return `
      <form class="card" data-step-form="complete">
        ${this.renderAlerts()}
        ${includeReview ? this.renderReviewSection() : ''}
        <div class="shipping-grid">
          ${this.renderSelectedSample()}
          <div>
            <p class="helper" style="margin:0 0 16px">We will ship your selected ${escapeHtml(this.copy.sampleSingular)} to:</p>
            ${this.renderAddressFields()}
          </div>
        </div>
        <div class="actions">
          <button class="primary" type="submit" ${this.submitting ? 'disabled' : ''}>
            ${this.submitting ? 'Submitting…' : `Complete ${escapeHtml(this.copy.shortName)}`}
          </button>
        </div>
      </form>
    `
  }

  private renderCompletion() {
    const pausedCopy = getCrmPausedAmazonReviewsCopy(this.flowType)
    if (this.amazonReviewsPaused) {
      return `
        <section class="card complete">
          <div class="check" aria-hidden="true">✓</div>
          <h1>${escapeHtml(pausedCopy.title)}</h1>
          <p>${escapeHtml(pausedCopy.message)}</p>
          <a class="home-link" href="/">Back to Home</a>
        </section>
      `
    }

    if (this.completionReason === 'NO_MATCHED_GIFT') {
      const processName = this.flowType === 'warranty' ? 'warranty activation' : 'product registration'
      const contactEmail = this.session?.site.contactEmail || 'customer service'
      return `
        <section class="card complete">
          <div class="check" aria-hidden="true">✓</div>
          <h1>Congratulations!</h1>
          <p>Your ${escapeHtml(processName)} is complete. If you have any questions, please contact <a href="mailto:${escapeHtml(contactEmail)}">${escapeHtml(contactEmail)}</a>.</p>
        </section>
      `
    }

    return `
      <section class="card complete">
        <div class="check" aria-hidden="true">✓</div>
        <h1>${escapeHtml(this.copy.completionTitle)}</h1>
        <p>${escapeHtml(this.copy.completionMessage)}</p>
      </section>
    `
  }

  private renderStep() {
    if (this.step === 1) return this.renderActivation()
    if (this.step === 2) return this.renderCongrats()
    if (this.step === 3) return this.renderReview()
    if (this.step === 4) return this.renderShare()
    if (this.step === 5) return this.renderAddress(false)
    return this.renderCompletion()
  }

  private renderHero() {
    if (this.step === 6) return ''
    const legacyShare = this.activeFlowContent
    const shareEyebrow = this.shareVisible('heroEyebrow')
      ? this.shareText('heroEyebrow', {}, legacyShare?.shareEyebrow || 'Step 2 of 3')
      : ''
    const shareTitle = this.shareVisible('heroTitle')
      ? this.shareText('heroTitle', { platform: platformLabels[this.form.platformKey] }, legacyShare?.shareTitle || this.copy.shareTitle)
      : ''
    const legacyShareDescription = legacyShare?.shareDescription
      ? `${legacyShare.shareDescription} ${legacyShare.platformContextPrefix || 'The current platform is'} ${platformLabels[this.form.platformKey]}.`
      : this.copy.shareDescription
    const shareDescription = this.shareVisible('heroDescription')
      ? this.shareText('heroDescription', { platform: platformLabels[this.form.platformKey] }, legacyShareDescription)
      : ''
    const titles = [
      [this.copy.eyebrow, this.copy.activationTitle, this.copy.activationDescription],
      ['Congratulations', this.copy.congratsTitle, this.copy.congratsMessage],
      ['Step 1 of 3', this.copy.experienceTitle, this.copy.experienceDescription],
      [shareEyebrow, shareTitle, shareDescription],
      ['Final Steps', this.copy.addressTitle, this.copy.addressDescription],
    ]
    const item = titles[Math.min(this.step, 5) - 1] || titles[0]
    return `
      <header class="hero">
        ${item?.[0] ? `<p class="eyebrow">${escapeHtml(item[0])}</p>` : ''}
        ${item?.[1] ? `<h1>${escapeHtml(item[1])}</h1>` : ''}
        ${item?.[2] ? `<p>${escapeHtml(item[2])}</p>` : ''}
      </header>
    `
  }

  private render() {
    if (this.initializing) {
      this.root.innerHTML = `<style>${styles}</style><div class="empty-bootstrap" aria-busy="true"></div>`
      return
    }

    if (!this.session) {
      this.root.innerHTML = `
        <style>${styles}</style>
        <section class="fatal" role="alert">
          <h2>This customer flow is unavailable</h2>
          <p>${escapeHtml(this.errorMessage || 'Please check the CRM integration configuration and try again.')}</p>
          <button class="secondary" type="button" data-action="retry">Try Again</button>
        </section>
      `
      return
    }

    this.root.innerHTML = `
      <style>${styles}</style>
      <main class="flow-shell" data-flow-type="${this.flowType}" data-flow-version="${this.flowVersion}" aria-busy="${this.submitting}">
        <div class="flow-container">
          <div class="brand-row">
            <div class="brand">${escapeHtml(this.session.site.brandName)}</div>
            <div class="version-badge">${this.flowVersion === 'detailed' ? 'Detailed flow' : 'Simple flow'}</div>
          </div>
          ${this.step >= 3 && this.step < 6 ? this.renderProgress() : ''}
          ${this.renderHero()}
          ${this.renderStep()}
        </div>
      </main>
    `
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('crm-customer-flow')) {
  customElements.define('crm-customer-flow', CrmCustomerFlowElement)
}

export { CrmEmbedApiError, CrmEmbedClient } from './crm-embed-client'
export type * from './crm-embed-types'
